import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { CalendarEvent } from '../../../hooks/useSupabaseEvents';
import { Calendar } from '../../../types';
import { COLORS } from '../../../utils/calendar/constants';
import { ConfirmDialog, type ConfirmDialogState } from '../../ui/ConfirmDialog';
import { useFocusTrap } from '../../../hooks/useFocusTrap';

interface TagManagerModalProps {
  calendars: Calendar[];
  events: CalendarEvent[];
  onAddTag: (tag: { name: string; color: string }) => Promise<void>;
  onUpdateTag: (id: string, updates: Partial<Calendar>) => Promise<void>;
  onDeleteTag: (id: string) => Promise<void>;
  onClose: () => void;
  onEventUpdate: (id: string | number, updates: Partial<CalendarEvent>) => Promise<any>;
  refetch: () => Promise<void>;
  selectedCalendar: Calendar | null;
  setSelectedCalendar: (tag: Calendar | null) => void;
}

export const TagManagerModal: React.FC<TagManagerModalProps> = ({
  calendars, events, onAddTag, onUpdateTag, onDeleteTag, onClose,
  onEventUpdate, refetch, selectedCalendar, setSelectedCalendar
}) => {
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, true);

  return (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeInBackdrop">
    <div ref={modalRef} role="dialog" aria-modal="true" className="bg-[var(--modal-bg)] rounded-xl w-full max-w-2xl max-h-[80vh] shadow-xl flex flex-col animate-fadeIn">
      <div className="px-6 py-4 border-b border-[var(--border-primary)] flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-[var(--content-primary)]">Manage Tags</h2>
          <p className="text-sm text-[var(--content-secondary)] mt-1">Create custom categories for your events</p>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors" aria-label="Close">
          <X className="w-5 h-5 text-[var(--content-tertiary)]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mb-6">
          <button
            onClick={async () => {
              await onAddTag({
                name: 'New Tag',
                color: COLORS[Math.floor(Math.random() * COLORS.length)].bg,
              });
            }}
            className="w-full py-3 border-2 border-dashed border-[var(--border-primary)] rounded-lg hover:border-[var(--accent-primary)] hover:bg-[var(--accent-subtle)] transition-colors flex items-center justify-center gap-2 text-[var(--content-secondary)]"
          >
            <span className="text-lg">+</span>
            Add New Tag
          </button>
        </div>

        <div className="space-y-3">
          {calendars.map((tag) => (
            <div key={tag.id} className="p-4 bg-[var(--surface-primary)] border border-[var(--border-primary)] rounded-lg hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <button className={`w-8 h-8 rounded-full ${tag.color} border-2 border-[var(--border-primary)] hover:border-[var(--content-tertiary)] transition-colors`} />
                  <div className="absolute left-0 bottom-10 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-lg shadow-lg p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[60]" style={{ width: '164px' }}>
                    <div className="grid grid-cols-6 gap-1.5">
                      {COLORS.map((color) => (
                        <button
                          key={color.name}
                          onClick={async () => {
                            await onUpdateTag(tag.id, { color: color.bg });
                          }}
                          className={`w-6 h-6 rounded-full ${color.bg} hover:scale-110 transition-transform flex-shrink-0`}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <input
                  type="text"
                  key={tag.id}
                  defaultValue={tag.name}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  onBlur={async (e) => {
                    const oldName = tag.name;
                    let newName = e.currentTarget.value.trim();
                    if (!newName || newName === oldName) return;
                    await onUpdateTag(tag.id, { name: newName });
                  }}
                  className="flex-1 px-3 py-1 bg-transparent text-[var(--content-primary)] border border-transparent hover:border-[var(--border-primary)] focus:border-[var(--accent-primary)] rounded transition-colors"
                />

                <button
                  onClick={() => {
                    setConfirmDialog({
                      title: 'Delete Tag',
                      message: `Delete "${tag.name}" tag? Events using this tag will become uncategorized.`,
                      confirmLabel: 'Delete',
                      destructive: true,
                      onConfirm: async () => {
                        await onDeleteTag(tag.id);
                        if (selectedCalendar?.id === tag.id) {
                          setSelectedCalendar(calendars[0] || null);
                        }
                      },
                    });
                  }}
                  className="p-1.5 text-[var(--content-tertiary)] hover:text-[var(--error)] hover:bg-[var(--error)]/10 rounded transition-colors"
                  aria-label="Delete tag"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    {confirmDialog && (
      <ConfirmDialog dialog={confirmDialog} onClose={() => setConfirmDialog(null)} />
    )}
  </div>
  );
};
